<template>
  <div class="panel-root stack tight">
    <UiSectionHeader>
      <template #title>{{ ICONS.itemsByType.resource }} Ground Loot</template>
      <template #actions>
        <UiTabs v-model="filter" :tabs="tabs" />
        <UiChip variant="muted" class="stat-mono num">{{ formatNumber(filteredLoot.length) }}</UiChip>
      </template>
    </UiSectionHeader>

    <UiCallout v-if="filteredLoot.length === 0" variant="info">
      <template v-if="filter === 'private'">No private loot.</template>
      <template v-else>No loot on the ground.</template>
    </UiCallout>

    <div v-else class="list panel-scroll scroll">
      <LootRow
        v-for="p in filteredLoot"
        :key="p.id"
        :pile="p"
        :disabled="disabled"
        :isPrivateToMe="isCurrentlyPrivateToMe(p)"
        @take="emit('take', $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { GroundLootPile } from "@idle-rpg/shared";
import { ICONS } from "../../lib/icons";
import { formatNumber } from "../../lib/format";
import LootRow from "../world/rows/LootRow.vue";
import UiCallout from "../ui/UiCallout.vue";
import UiChip from "../ui/UiChip.vue";
import UiSectionHeader from "../ui/UiSectionHeader.vue";
import UiTabs from "../ui/UiTabs.vue";

const props = defineProps<{
  groundLoot: GroundLootPile[];
  disabled: boolean;
  currentCharacterId: string;
}>();

const emit = defineEmits<{
  take: [pileId: string];
}>();

const filter = ref<"all" | "private">("all");
const tabs = computed(() => [
  { value: "all", label: "All", icon: ICONS.ui.globe },
  { value: "private", label: "Private", icon: ICONS.ui.lock }
]);

function parseTimestamp(ts: string): number {
  if (!ts) return 0;
  const normalized = ts.includes("T") ? ts : `${ts.replace(" ", "T")}Z`;
  const ms = Date.parse(normalized);
  return Number.isFinite(ms) ? ms : 0;
}

function isCurrentlyPrivateToMe(pile: GroundLootPile): boolean {
  if (!pile.ownerId) return false;
  if (pile.ownerId !== props.currentCharacterId) return false;
  return parseTimestamp(pile.publicAt) > Date.now();
}

function stackValue(pile: GroundLootPile): number {
  const unit = pile.referencePrice?.price ?? pile.item?.basePrice ?? 0;
  return unit * (pile.qty ?? 0);
}

const filteredLoot = computed(() => {
  const list = filter.value === "private"
    ? props.groundLoot.filter((p) => isCurrentlyPrivateToMe(p))
    : props.groundLoot;

  return [...list].sort((a, b) => {
    const diff = stackValue(b) - stackValue(a);
    if (diff !== 0) return diff;
    return a.itemName.localeCompare(b.itemName);
  });
});
</script>

<style scoped>
.panel-root {
  min-width: 0;
}

.list {
  display: grid;
  gap: 0.6rem;
}
</style>
